import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { BehaviorSubject, Observable, of, switchMap, take, tap } from 'rxjs';
import { TranslationsDtoPipe } from 'src/core/pipes/translations-dto.pipe';
import { DialogService } from 'src/core/services';
import { AuthStore } from 'src/core/stores';
import {
  BlogEntryDto,
  CoreBlogEntriesService,
  CoreBlogsService,
  CreateBlogEntryDto,
  GetBlogEntriesRequestParams,
  GetBlogsRequestParams,
} from 'src/generated/api/nestjs-backend';
import { environment } from 'src/main/environments';
import { InformationCardComponent } from '../shared/information-card/information-card.component';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { EditBlogpostDialogComponent } from './edit/edit-blogpost-dialog.component';

@Component({
  selector: 'core-news-blog',
  templateUrl: './news-blog.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoDirective,
    TranslationsDtoPipe,
    InformationCardComponent,
  ],
})
export class NewsBlogComponent implements OnInit {
  private authStore = inject(AuthStore);
  private dialogService = inject(DialogService);
  private coreBlogsService = inject(CoreBlogsService);
  private coreBlogEntriesService = inject(CoreBlogEntriesService);

  @ViewChild(SidebarComponent) sidebar!: SidebarComponent;

  private isLoading$$ = new BehaviorSubject<boolean>(true);
  isLoading$ = this.isLoading$$.asObservable();

  private getBlogsRequestParams$$ = new BehaviorSubject<GetBlogsRequestParams>({
    xTenantSlug: this.authStore.tenantSlug(),
  });
  getBlogsRequestParams$ = this.getBlogsRequestParams$$.asObservable();
  getBlogs200Response$ = this.getBlogs200ResponseObservable();

  private getBlogEntriesRequestParams$$ =
    new BehaviorSubject<GetBlogEntriesRequestParams | null>(null);
  getBlogEntriesRequestParams$ =
    this.getBlogEntriesRequestParams$$.asObservable();
  getBlogEntries200Response$ = this.getBlogEntries200ResponseObservable();

  layoutColumns = environment.newsLayoutColumns;
  ngOnInit() {
    this.getBlogs200Response$.pipe(take(1)).subscribe();
  }

  changeRequest(request: Omit<GetBlogEntriesRequestParams, 'xTenantSlug'>) {
    this.getBlogEntriesRequestParams$$.next({
      ...this.getBlogEntriesRequestParams$$.value!,
      ...request,
    });
  }

  changePage(page: number) {
    this.changeRequest({ page });
  }

  getPages(length: number): number[] {
    return Array.from({ length }, (_, i) => i + 1);
  }

  deleteBlogPost(blogpost: BlogEntryDto) {
    const modalRef = this.dialogService.open(EditBlogpostDialogComponent, {
      context: { newsItem: blogpost, createOrEditDialog: false },
    });

    this.openModal(modalRef);
  }

  openCreateModal() {
    const newBlogEntry: CreateBlogEntryDto = {
      published: true,
      publishedAt: new Date().toISOString(),
      blogUuid: this.getBlogEntriesRequestParams$$.value!.blogUuid!,
      authorUserUuid: this.authStore.currentUser()!.uuid,
    };

    const modalRef = this.dialogService.open(EditBlogpostDialogComponent, {
      context: { newsItem: newBlogEntry, createOrEditDialog: true },
    });

    this.openModal(modalRef);
  }

  openEditModal(blogpost: BlogEntryDto) {
    const modalRef = this.dialogService.open(EditBlogpostDialogComponent, {
      context: { newsItem: blogpost, createOrEditDialog: true },
    });

    this.openModal(modalRef);
  }

  openModal(modalRef: {
    onClose: Observable<unknown>;
    componentRef: ComponentRef<unknown>;
  }) {
    modalRef.onClose.pipe(take(1)).subscribe((success: unknown) => {
      if (success) {
        this.changeRequest({});
      }
      modalRef.componentRef.destroy();
    });
  }

  private getBlogs200ResponseObservable() {
    return this.getBlogsRequestParams$$.pipe(
      switchMap((getBlogsRequestParams) =>
        this.coreBlogsService.getBlogs(getBlogsRequestParams),
      ),
      tap((getBlogs200Response) => {
        this.getBlogEntriesRequestParams$$.next({
          xTenantSlug: this.authStore.tenantSlug(),
          page: 1,
          size: 3,
          sortBy: 'publishedAt',
          sortOrder: 'asc',
          blogUuid: getBlogs200Response.data[0].uuid,
        });
      }),
    );
  }

  private getBlogEntries200ResponseObservable() {
    return this.getBlogEntriesRequestParams$$.pipe(
      tap(() => this.authStore.setIsLoading(true)),
      switchMap((getBlogEntriesRequestParams) => {
        if (getBlogEntriesRequestParams) {
          return this.coreBlogEntriesService.getBlogEntries(
            getBlogEntriesRequestParams,
          );
        }
        return of(null);
      }),
      tap(() => this.authStore.setIsLoading(false)),
    );
  }
}
